import { useState } from "react";
import { Check, Copy } from "lucide-react";

function CopyValue({ label, value, testid }) {
  const [done, setDone] = useState(false);
  const copy = async () => { try { await navigator.clipboard.writeText(value); setDone(true); setTimeout(() => setDone(false), 1500); } catch { /* clipboard blocked */ } };
  return (
    <span className="inline-flex items-center gap-1 flex-wrap">
      {label && <span className="text-stone-500">{label}</span>}
      <code className="bg-stone-100 px-1.5 py-0.5 rounded text-[11px] break-all" data-testid={testid}>{value}</code>
      <button type="button" onClick={copy} data-testid={`${testid}-copy`} aria-label={`Copy ${label || value}`} className="text-stone-500 hover:text-stone-900">
        {done ? <Check className="w-3 h-3 text-emerald-700" /> : <Copy className="w-3 h-3" />}
      </button>
    </span>
  );
}

function Tick({ ok }) {
  return <span className={`mt-0.5 w-4 h-4 rounded-full grid place-items-center shrink-0 ${ok ? "bg-emerald-600 text-white" : "border border-stone-300 text-transparent"}`}><Check className="w-3 h-3" /></span>;
}

/** Where the monthly copy lives — the one-time Google Cloud setup, with every value Google asks for ready to copy. */
export function DriveSetupGuide({ st }) {
  const s = st.setup;
  const steps = [
    { ok: true, text: <>Sign in to <strong>console.cloud.google.com</strong> as the Workspace account that owns the Drive. Create a project named <CopyValue value={s.app_name} testid="drive-setup-app-name" />.</> },
    { ok: true, text: <>APIs &amp; Services → Library → enable <strong>Google Drive API</strong>.</> },
    { ok: true, text: <>OAuth consent screen → User type <strong>Internal</strong> (no verification needed inside a Workspace). App name <CopyValue value={s.app_name} testid="drive-setup-app-name-2" />, support email <CopyValue value={s.support_email} testid="drive-setup-email" />. Add only this scope: <CopyValue value={s.scope} testid="drive-setup-scope" />.</> },
    { ok: true, text: <>Credentials → Create credentials → OAuth client ID → Application type <strong>{s.client_type}</strong> (a web app never asks for a Bundle ID). Name <CopyValue value={s.client_name} testid="drive-setup-client-name" />. Authorised redirect URI — paste exactly: <CopyValue value={s.redirect_uri} testid="drive-setup-redirect" />.</> },
    { ok: st.configured, text: <>Copy the Client ID and Client Secret into this app's secure key card as <CopyValue value={s.keys[0]} testid="drive-setup-key-id" /> and <CopyValue value={s.keys[1]} testid="drive-setup-key-secret" /> (live slot). The card below then shows <em>Connect Google Drive</em>.</> },
    { ok: st.connected, text: <>Connect Google Drive → approve as the Workspace account.</> },
    { ok: !!st.last_copy, text: <>Send a test copy now → open the Drive link and confirm the file sits in “{st.folder}”.</> },
  ];
  return (
    <details className="text-xs text-stone-700" data-testid="drive-setup-guide" open={!st.connected}>
      <summary className="cursor-pointer font-semibold text-stone-800">Where the monthly copy lives — one-time Google setup (about 10 minutes)</summary>
      <ol className="mt-2 space-y-2">
        {steps.map((step, i) => (
          <li key={i} className="flex items-start gap-2" data-testid={`drive-setup-step-${i + 1}`}>
            <Tick ok={step.ok} /><span className="leading-relaxed">{step.text}</span>
          </li>
        ))}
      </ol>
      <p className="mt-2 text-[11px] text-stone-500">Register the production URI only — the preview workspace is never connected. Archives stay sealed with this app's key; a copy taken from Drive is readable only through Restore.</p>
    </details>
  );
}
